// src/context/FormTypes.js - Action types and initial state

// Action types
export const ActionTypes = {
  SET_PATIENT_DATA: 'SET_PATIENT_DATA',
  SET_CANALS_TRACED: 'SET_CANALS_TRACED',
  SET_PANORAMIC_DATA: 'SET_PANORAMIC_DATA',
  SET_DIMENSIONS_DATA: 'SET_DIMENSIONS_DATA',
  SET_REGION_DATA: 'SET_REGION_DATA',
  UPDATE_REGION_DATA: 'UPDATE_REGION_DATA',
  REMOVE_REGION_DATA: 'REMOVE_REGION_DATA',
  SET_REPORT_TYPE: 'SET_REPORT_TYPE',
  SET_SELECTED_UNIT: 'SET_SELECTED_UNIT',
  SET_SELECTED_DOCTOR: 'SET_SELECTED_DOCTOR',
  SET_SELECTED_FILE: 'SET_SELECTED_FILE',
  SET_CURRENT_TAB: 'SET_CURRENT_TAB',
  SET_SIDEBAR_OPEN: 'SET_SIDEBAR_OPEN',
  TOGGLE_SIDEBAR: 'TOGGLE_SIDEBAR',
  RESET_FORM: 'RESET_FORM',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_FORM_DATA: 'CLEAR_FORM_DATA',
  SET_RADIOLOGICAL_IMPRESSIONS: 'SET_RADIOLOGICAL_IMPRESSIONS',
  SET_SINUS_VIEW_DATA: 'SET_SINUS_VIEW_DATA',
  SET_SAGITTAL_VIEW_DATA: 'SET_SAGITTAL_VIEW_DATA',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_OTHER_IMPRESSION: 'SET_OTHER_IMPRESSION',
  SET_IS_RESTORING: 'SET_IS_RESTORING',
  RESTORE_STATE: 'RESTORE_STATE',

  // Persistent UI state actions
  SET_DIMENSIONS_PDF_STATE: 'SET_DIMENSIONS_PDF_STATE',
  UPDATE_DIMENSIONS_PDF_STATE: 'UPDATE_DIMENSIONS_PDF_STATE',
  SET_REGION_DETAILS_PDF_STATE: 'SET_REGION_DETAILS_PDF_STATE',
  UPDATE_REGION_DETAILS_PDF_STATE: 'UPDATE_REGION_DETAILS_PDF_STATE',
  SET_PANORAMIC_UI_STATE: 'SET_PANORAMIC_UI_STATE',
  UPDATE_PANORAMIC_UI_STATE: 'UPDATE_PANORAMIC_UI_STATE',
  SET_CANALS_UI_STATE: 'SET_CANALS_UI_STATE',
  UPDATE_CANALS_UI_STATE: 'UPDATE_CANALS_UI_STATE',
  SET_REGION_FORM_STATE: 'SET_REGION_FORM_STATE',
  UPDATE_REGION_FORM_STATE: 'UPDATE_REGION_FORM_STATE',

  // Image URL management actions
  RESTORE_BLOB_URLS: 'RESTORE_BLOB_URLS',
  CLEANUP_BLOB_URLS: 'CLEANUP_BLOB_URLS',
  AUTO_RESTORE_IMAGES: 'AUTO_RESTORE_IMAGES',
  SET_IMPACTION_DATA: 'SET_IMPACTION_DATA'
};

// Initial state
export const initialState = {
  // Patient and file data
  patientData: null,
  selectedFile: null,
  selectedUnit: 'UNIT Number',
  selectedDoctor: 'Doctor Name',

  // Report configuration
  reportType: 'Mandible - Basic Report',
  currentTab: 0,
  isSidebarOpen: true,

  // Form data
  canalsTraced: '',
  impactionData: {},
  panoramicViewData: {
    panoramicFile1: null,
    panoramicFile2: null,
    comments: ''
  },
  dimensionsData: {
    pdfFile: null,
    croppedImage1: null,
    croppedImage2: null
  },
  regionDetailsData: {},
  sinusViewData: {
    image: null,
    selectedImpressions: [],
    otherImpression: ''
  },
  sagittalViewData: {
    image: null,
    mainComment: '',
    mandibleComment: ''
  },
  radiologicalImpressions: [],
  otherImpression: '',

  // UI state
  loading: false,
  error: null,
  isRestoring: false,

  // Persistent UI states for tabs
  dimensionsPdfState: {
    pdfFile: null,
    convertedImages: [],
    currentImageIndex: 0,
    crop: {
      unit: '%',
      width: 30,
      height: 30,
      x: 25,
      y: 25
    },
    completedCrop: null,
    croppedImageUrl1: null,
    croppedImageUrl2: null,
    croppedImageFile1: null,
    croppedImageFile2: null,
    loading: false,
    error: null
  },

  regionDetailsPdfState: {
    pdfFile: null,
    convertedImages: [],
    expanded: [],
    selected: [],
    loadingPdf: false,
    error: null
  },

  panoramicViewUIState: {
    preview1: null,
    preview2: null,
    selectedComments: [],
    otherComments: ''
  },

  canalsUIState: {
    selectedCanals: [],
    otherCanals: ''
  },

  regionFormStates: {},

  // Image state tracking
  imageStateHistory: {
    lastRestoreTime: 0,
    restoredUrls: new Set(),
    needsRestore: false
  }
};

// Blob URL utility functions
export const createBlobUrl = (file) => {
  // Enhanced validation for File and Blob objects
  if (file && typeof file === 'object') {
    // Check if it's a File object
    if (file instanceof File) {
      try {
        const url = URL.createObjectURL(file);
        console.log('Created blob URL from File:', url.substring(0, 50) + '...');
        return url;
      } catch (error) {
        console.warn('Failed to create blob URL from File:', error);
        return null;
      }
    }
    // Check if it's a Blob object
    else if (file instanceof Blob) {
      try {
        const url = URL.createObjectURL(file);
        console.log('Created blob URL from Blob:', url.substring(0, 50) + '...');
        return url;
      } catch (error) {
        console.warn('Failed to create blob URL from Blob:', error);
        return null;
      }
    }
    // Check if it has the File constructor (fallback for different contexts)
    else if (file.constructor && file.constructor.name === 'File') {
      try {
        const url = URL.createObjectURL(file);
        console.log('Created blob URL from File constructor:', url.substring(0, 50) + '...');
        return url;
      } catch (error) {
        console.warn('Failed to create blob URL from File constructor:', error);
        return null;
      }
    }
    else {
      console.warn('createBlobUrl called with invalid object type:', typeof file, file.constructor?.name, file);
      return null;
    }
  }
  console.warn('createBlobUrl called with invalid parameter:', typeof file, file);
  return null;
};

export const revokeBlobUrl = (url) => {
  if (url && typeof url === 'string' && url.startsWith('blob:')) {
    try {
      URL.revokeObjectURL(url);
      console.log('Revoked blob URL:', url.substring(0, 50) + '...');
    } catch (error) {
      console.warn('Failed to revoke blob URL:', error);
    }
  }
};

// Auto-restore function
export const autoRestoreImages = (state) => {
  const updates = {};
  let hasUpdates = false;

  console.log('autoRestoreImages: Starting image restoration...');

  // Restore panoramic images
  if (state.panoramicViewData?.panoramicFile1 && !state.panoramicViewUIState?.preview1) {
    console.log('Restoring panoramic image 1...');
    const newUrl = createBlobUrl(state.panoramicViewData.panoramicFile1);
    if (newUrl) {
      updates.panoramicViewUIState = {
        ...state.panoramicViewUIState,
        preview1: newUrl
      };
      hasUpdates = true;
      console.log('Successfully restored panoramic image 1');
    } else {
      console.warn('Failed to restore panoramic image 1');
    }
  }

  if (state.panoramicViewData?.panoramicFile2 && !state.panoramicViewUIState?.preview2) {
    console.log('Restoring panoramic image 2...');
    const newUrl = createBlobUrl(state.panoramicViewData.panoramicFile2);
    if (newUrl) {
      updates.panoramicViewUIState = {
        ...updates.panoramicViewUIState || state.panoramicViewUIState,
        preview2: newUrl
      };
      hasUpdates = true;
      console.log('Successfully restored panoramic image 2');
    } else {
      console.warn('Failed to restore panoramic image 2');
    }
  }

  // Restore region images
  const regionFormStates = { ...state.regionFormStates };
  Object.keys(regionFormStates).forEach(regionNumber => {
    const regionState = regionFormStates[regionNumber];
    if (!regionState) {
      console.warn(`No region state found for region ${regionNumber}`);
      return;
    }

    let regionUpdates = {};
    let hasRegionUpdates = false;

    console.log(`Checking region ${regionNumber} for image restoration...`);

    if (regionState.croppedImageFile1 && !regionState.croppedImageUrl1) {
      console.log(`Restoring region ${regionNumber} image 1...`);
      const newUrl = createBlobUrl(regionState.croppedImageFile1);
      if (newUrl) {
        regionUpdates.croppedImageUrl1 = newUrl;
        hasRegionUpdates = true;
        console.log(`Successfully restored region ${regionNumber} image 1`);
      } else {
        console.warn(`Failed to restore region ${regionNumber} image 1`);
      }
    }

    if (regionState.croppedImageFile2 && !regionState.croppedImageUrl2) {
      console.log(`Restoring region ${regionNumber} image 2...`);
      const newUrl = createBlobUrl(regionState.croppedImageFile2);
      if (newUrl) {
        regionUpdates.croppedImageUrl2 = newUrl;
        hasRegionUpdates = true;
        console.log(`Successfully restored region ${regionNumber} image 2`);
      } else {
        console.warn(`Failed to restore region ${regionNumber} image 2`);
      }
    }

    if (hasRegionUpdates) {
      regionFormStates[regionNumber] = {
        ...regionState,
        ...regionUpdates
      };
      hasUpdates = true;
    }
  });

  if (hasUpdates) {
    updates.regionFormStates = regionFormStates;
    updates.imageStateHistory = {
      ...state.imageStateHistory,
      lastRestoreTime: Date.now(),
      needsRestore: false
    };
    console.log('autoRestoreImages: Restoration completed with updates');
  } else {
    console.log('autoRestoreImages: No images needed restoration');
  }

  return { hasUpdates, updates };
};

// Default region form state
export const getDefaultRegionFormState = () => ({
  currentImageIndex: 0,
  crop: { unit: '%', width: 30, height: 30, x: 25, y: 25 },
  completedCrop: null,
  croppedImageUrl1: null,
  croppedImageUrl2: null,
  croppedImageFile1: null,
  croppedImageFile2: null,
  formData: {
    pathology_comments: '',
    length_crest: '',
    length_crest_to: '___',
    length_periapical: '',
    length_periapical_to: '___',
    length_lesion: '',
    length_incision: '',
    length_quality: '___',
    ridge_form: '___',
    undercut: '___',
    additional_comments: '',
    width_crest: '',
    width_option: '___',
    width_quality: '___',
    bone_quality: {
      crestal: '___________',
      buccal: '___________',
      lingual: '___________',
      palatal: '___________',
      labial: '___________',
      cancellous: '___________'
    }
  },
  optionalSliceData: {
    showMentalForamen: false,
    showLingualCanal: false,
    showNasopalatine: false,
    sliceContent: '',
    sliceImageFile: null
  }
});